import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { FUNCTIONS_RESOURCE, PEOPLE_KEY, type FunctionSummary } from '../people/types';
import { REQUESTS_KEY, UNSENT_KEY, shortDateTime, type Recipient, type SendResult } from './types';
import { refreshQueries } from '../refresh';
import './requests.css';
import { DialogLayer } from '../ui/Dialog';
import { recipientsText } from './recipients';

/** Polish message of a failed request; validation errors list their fields' messages. */
function errorText(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Nie udało się wysłać zapytania';
  const details = Array.isArray(error.details) ? (error.details as { message?: unknown }[]).map((d) => d.message).filter((m): m is string => typeof m === 'string') : [];
  return details.length > 0 ? details.join('. ') : error.message;
}

/** The names the user may see; without the right to see people only how many. */
const names = (list: { name?: string }[]) => (list.every((p) => p.name) ? list.map((p) => p.name).join(', ') : `${list.length} ${list.length === 1 ? 'osoba' : 'osób'}`);

/**
 * "Zapytanie o dostępność": to people of the crew (ticked) and/or everyone with a function, with a message. Opened
 * by "Wyślij zapytanie", or by itself after new people were added to the crew (`afterAdd`: "Tak, wyślij" / "Nie").
 * People already asked about this event cannot be ticked (the server would skip them anyway).
 * Owner 2026-10-27 12:26: before „Wyślij” the dialog says whom it reaches — the ticked people and everyone with the
 * function, by the server (`POST /api/requests/recipients`, nothing sent): „Wyślę do: …” (rule 7a: how many).
 */
export function SendRequestDialog({
  event,
  people,
  asked,
  initialPersonIds,
  initialFunctionId,
  afterAdd = false,
  onClose,
}: {
  event: { id: string; name?: string };
  /** The crew, one entry per person. */
  people: { personId: string; personName: string }[];
  /** Who was asked about this event already (from `/api/requests/events/:id`). */
  asked: Recipient[];
  initialPersonIds?: string[];
  /** Opened from an open place of the crew: that function chosen, nobody of the crew ticked (unless `initialPersonIds`). */
  initialFunctionId?: string;
  afterAdd?: boolean;
  onClose: () => void;
}) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  // stage 2: a stale question (the dates changed) or a question about days does not count — the person may be asked again
  const askedAt = new Map(asked.filter((r) => !r.stale && r.kind !== 'dates').map((r) => [r.personId, r.sentAt]));
  const notAsked = people.filter((p) => !askedAt.has(p.personId)).map((p) => p.personId);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set((initialPersonIds ?? (initialFunctionId ? [] : notAsked)).filter((id) => !askedAt.has(id))));
  const [functionId, setFunctionId] = useState(initialFunctionId ?? '');
  const [message, setMessage] = useState(`Czy możesz pracować przy wydarzeniu „${event.name || 'Wydarzenie'}”?`);
  const mayFunctions = !!me && can(me, FUNCTIONS_RESOURCE, 'view');
  const functions = useQuery({ queryKey: [...PEOPLE_KEY, 'functions'], enabled: mayFunctions, queryFn: () => api<{ functions: FunctionSummary[] }>('/api/people/functions') });

  const previewBody = { eventId: event.id, ...(chosen.size > 0 ? { personIds: [...chosen] } : {}), ...(functionId ? { functionId } : {}) };
  // review M2: read afresh each time; the send carries this list (changed meanwhile: 409, read again)
  const preview = useQuery({
    queryKey: [...REQUESTS_KEY, 'recipients', previewBody],
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: chosen.size > 0 || functionId !== '',
    queryFn: () => api<{ recipients: { personId: string; name?: string }[]; skipped: { personId: string; name?: string }[]; noContact: { personId: string; name?: string }[] }>('/api/requests/recipients', { method: 'POST', body: previewBody }),
  });
  const reach = preview.data?.recipients;
  const send = useMutation({
    mutationFn: () =>
      api<SendResult>('/api/requests', {
        method: 'POST',
        body: { eventId: event.id, message: message.trim(), ...(chosen.size > 0 ? { personIds: [...chosen] } : {}), ...(functionId ? { functionId } : {}), ...(reach ? { expected: reach.map((r) => r.personId) } : {}) },
      }),
    // review I2: the sent people leave „Do wysłania” over the grid
    onSuccess: () => Promise.all([queryClient.invalidateQueries({ queryKey: REQUESTS_KEY }), refreshQueries(queryClient, UNSENT_KEY)]),
    onError: async (e) => {
      if (e instanceof ApiError && e.status === 409) await preview.refetch();
    },
  });
  const changed = send.error instanceof ApiError && send.error.status === 409;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };
  const toggle = (id: string, on: boolean) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const result = send.data;
  if (result) {
    return (
      <div className="dialog-backdrop">
        <div className="dialog panel" role="dialog" aria-modal="true" aria-labelledby="request-sent-title" data-feature-id="requests.send">
          <DialogLayer onClose={onClose} />
          <h2 id="request-sent-title">{result.sent.length > 0 ? 'Zapytanie wysłane' : 'Nic nie wysłano'}</h2>
          {result.sent.length > 0 && <p>Wysłano do: {names(result.sent)}.</p>}
          {result.skipped.length > 0 && (
            <p className="muted">
              Już zapytani o to wydarzenie (bez drugiego zapytania): {result.skipped.every((s) => s.name) ? result.skipped.map((s) => `${s.name}${s.sentAt ? ` (${shortDateTime(s.sentAt)})` : ''}`).join(', ') : names(result.skipped)}.
            </p>
          )}
          {result.noContact.length > 0 && (
            <p className="alert" role="alert">
              Bez konta, e-maila i telefonu — nie da się zapytać: {names(result.noContact)}. Uzupełnij kontakt w Osoby.
            </p>
          )}
          <div className="actions">
            <button className="btn primary" type="button" onClick={onClose} autoFocus>
              Zamknij
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fnList = functions.data?.functions ?? [];
  const nothingChosen = chosen.size === 0 && functionId === '';
  return (
    <div className="dialog-backdrop">
      <form className="dialog panel request-dialog" role="dialog" aria-modal="true" aria-labelledby="request-title" onSubmit={submit} data-feature-id="requests.send">
        <DialogLayer onClose={onClose} busy={send.isPending} />
        <h2 id="request-title">{afterAdd ? 'Wysłać zapytanie o dostępność?' : 'Zapytanie o dostępność'}</h2>
        <p className="muted">Osoba dostanie link (dzwonek, e-mail albo SMS) i odpowie „tak” albo „nie” dla każdego dnia wydarzenia (albo zada pytanie) — bez logowania.</p>
        {people.length > 0 && (
          <fieldset className="request-people">
            <legend>Osoby z obsady</legend>
            {people.map((p) => {
              const sentAt = askedAt.get(p.personId);
              return (
                <label key={p.personId} className="check">
                  <input type="checkbox" checked={chosen.has(p.personId)} disabled={askedAt.has(p.personId)} onChange={(e) => toggle(p.personId, e.target.checked)} />
                  <span>
                    {p.personName}
                    {askedAt.has(p.personId) && <span className="muted"> — zapytano{sentAt ? ` ${shortDateTime(sentAt)}` : ''}</span>}
                  </span>
                </label>
              );
            })}
          </fieldset>
        )}
        {mayFunctions && !afterAdd && (
          <label className="field">
            <span>{people.length > 0 ? 'Albo też wszyscy z funkcją' : 'Wszyscy z funkcją'}</span>
            <select value={functionId} onChange={(e) => setFunctionId(e.target.value)}>
              <option value="">—</option>
              {fnList.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name ?? 'Funkcja'}
                </option>
              ))}
            </select>
          </label>
        )}
        {mayFunctions && !afterAdd && functionId !== '' && (
          <p className="muted">Zapytanie dostaną wszyscy z funkcją „{fnList.find((f) => f.id === functionId)?.name ?? 'Funkcja'}” (kogo już zapytano, ten nie dostanie drugiego).</p>
        )}
        {(chosen.size > 0 || functionId !== '') && (
          <p className="recipients" aria-live="polite">
            {preview.isLoading ? 'Sprawdzam, do kogo…' : reach ? recipientsText(reach) : ''}
            {preview.error && (
              <span className="alert" role="alert">
                {preview.error instanceof ApiError ? preview.error.message : 'Nie udało się sprawdzić, do kogo pójdzie zapytanie'}
              </span>
            )}
            {(preview.data?.skipped ?? []).length > 0 && <span className="muted"> · Już zapytani (nie dostaną drugiego): {names(preview.data!.skipped)}</span>}
            {(preview.data?.noContact ?? []).length > 0 && <span className="muted"> · Bez kontaktu (nie dostaną): {names(preview.data!.noContact)}</span>}
          </p>
        )}
        <label className="field">
          <span>Treść zapytania</span>
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} maxLength={2000} required />
        </label>
        <p className="muted">Osoba zobaczy też nazwę wydarzenia, termin i miejsce — bez klienta i kwot.</p>
        {send.error && (
          <p className="alert" role="alert">
            {changed ? 'Lista adresatów się zmieniła — sprawdź ją i wyślij ponownie.' : errorText(send.error)}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={send.isPending || nothingChosen || message.trim() === '' || (!!reach && reach.length === 0) || !!preview.error || preview.isFetching}>
            {send.isPending ? 'Wysyłanie…' : afterAdd ? 'Tak, wyślij' : 'Wyślij zapytanie'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            {afterAdd ? 'Nie' : 'Anuluj'}
          </button>
        </div>
      </form>
    </div>
  );
}
